import { Inject, Injectable, InjectionToken } from '@angular/core';
import { PaginatedStateEffects } from '@shared/ui';
import type {
  IAnyMethodTypesForResourceQuery,
  ICountResourcesQuery,
  IDeleteResourceCommand,
  IListResourcesQuery,
} from '@protocols/use-cases';
import { combineLatest, firstValueFrom, from, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ResourceData, ResourceState, ResourceStateModel } from '../state/resource.state';
import { StagedResourcesState } from '../state/staged-resources.state';

export const COUNT_RESOURCES_QUERY = new InjectionToken<ICountResourcesQuery>('COUNT_RESOURCES_QUERY');
export const LIST_RESOURCES_QUERY = new InjectionToken<IListResourcesQuery>('LIST_RESOURCES_QUERY');
export const DELETE_RESOURCE_COMMAND = new InjectionToken<IDeleteResourceCommand>('DELETE_RESOURCE_COMMAND');
export const ANY_METHOD_TYPES_FOR_RESOURCE_QUERY = new InjectionToken<IAnyMethodTypesForResourceQuery>(
  'ANY_METHOD_TYPES_FOR_RESOURCE_QUERY'
);

@Injectable()
export class ResourceEffects extends PaginatedStateEffects<ResourceData, ResourceStateModel> {
  constructor(
    public readonly resourceState: ResourceState,
    private readonly stagedState: StagedResourcesState,
    @Inject(COUNT_RESOURCES_QUERY) private readonly countResourcesQuery: ICountResourcesQuery,
    @Inject(LIST_RESOURCES_QUERY) private readonly listResourcesQuery: IListResourcesQuery,
    @Inject(DELETE_RESOURCE_COMMAND) private readonly deleteResourceCommand: IDeleteResourceCommand,
    @Inject(ANY_METHOD_TYPES_FOR_RESOURCE_QUERY)
    private readonly anyMethodTypesForResourceQuery: IAnyMethodTypesForResourceQuery
  ) {
    super(resourceState);
  }

  // ============================================================================
  // Re-exposed Selectors from State
  // ============================================================================

  public readonly resources$ = this.resourceState.resources$;
  public readonly nextResourceToken$ = this.resourceState.nextResourceToken$;
  public readonly previousResourceTokens$ = this.resourceState.previousResourceTokens$;
  public readonly resourcesLoaded$ = this.resourceState.resourcesLoaded$;
  public readonly noResources$ = this.resourceState.noResources$;
  public readonly typeFilter$ = this.resourceState.typeFilter$;
  public readonly pendingResources$ = this.stagedState.pendingResources$;
  public readonly resourceOverrides$ = this.stagedState.resourceOverrides$;

  // ============================================================================
  // Backwards-compat effect aliases
  // ============================================================================

  public readonly deleteResource = this.deleteItem;
  public readonly loadMoreResources = this.loadMoreItems;
  public readonly initializeResources = this.initializeItems;
  public readonly countResources = this.countItems;

  // ============================================================================
  // Re-exposed Updaters from State
  // ============================================================================

  public readonly setSearchTerm = this.resourceState.setSearchTerm;
  public readonly setTypeFilter = this.resourceState.setTypeFilter;
  public readonly changePageSize = this.resourceState.changePageSize;
  public readonly addPendingResource = this.stagedState.addPendingResource;
  public readonly removePendingResource = this.stagedState.removePendingResource;
  public readonly clearPendingResources = this.stagedState.clearPendingResources;
  public readonly setResourceOverride = this.stagedState.setResourceOverride;
  public readonly removeResourceOverride = this.stagedState.removeResourceOverride;
  public readonly clearResourceOverrides = this.stagedState.clearResourceOverrides;

  // ============================================================================
  // Store-specific methods
  // ============================================================================

  public canBeDeleted(item: ResourceData): Observable<boolean> {
    return from(this.anyMethodTypesForResourceQuery.execute(item.id)).pipe(map((any) => !any));
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected async countRepository(searchTerm?: string): Promise<number> {
    const [typeFilter, pendingResources, resourceOverrides] = await firstValueFrom(
      combineLatest([
        this.resourceState.typeFilter$,
        this.stagedState.pendingResources$,
        this.stagedState.resourceOverrides$,
      ])
    );
    const dbCount = await this.countResourcesQuery.execute(searchTerm, typeFilter);
    const pendingCount = pendingResources.filter(
      (p) => this.matchesSearch(p, searchTerm) && this.matchesType(p, typeFilter)
    ).length;

    // Gate both halves on type so the staged delta matches the type-filtered
    // dbCount: a non-matching original is no longer in dbCount, so it must not
    // be subtracted either
    const overrideDelta = Object.values(resourceOverrides).reduce(
      (acc, { original, override }) =>
        acc +
        (this.matchesSearch(override, searchTerm) && this.matchesType(override, typeFilter) ? 1 : 0) -
        (this.matchesSearch(original, searchTerm) && this.matchesType(original, typeFilter) ? 1 : 0),
      0
    );
    return dbCount + pendingCount + overrideDelta;
  }

  protected async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      await this.deleteResourceCommand.execute(itemId);
      return true;
    } catch {
      return false;
    }
  }

  protected listFlow(
    limit: number,
    lastItem: ResourceData | undefined,
    searchTerm: string | undefined
  ): Observable<ResourceData[]> {
    return combineLatest([
      this.resourceState.typeFilter$,
      this.stagedState.pendingResources$,
      this.stagedState.resourceOverrides$,
    ]).pipe(
      take(1),
      switchMap(([typeFilter, pendingResources, resourceOverrides]) => {
        const overrideIds = new Set(Object.keys(resourceOverrides));
        const cursor = lastItem?.name;

        const pendingPage = pendingResources
          .filter((p) => this.matchesSearch(p, searchTerm))
          .filter((p) => this.matchesType(p, typeFilter))
          .filter((p) => cursor === undefined || p.name.localeCompare(cursor) > 0)
          .sort((a, b) => a.name.localeCompare(b.name));

        const overridesPage = Object.values(resourceOverrides)
          .map((entry) => entry.override)
          .filter((o) => this.matchesSearch(o, searchTerm))
          .filter((o) => this.matchesType(o, typeFilter))
          .filter((o) => cursor === undefined || o.name.localeCompare(cursor) > 0)
          .sort((a, b) => a.name.localeCompare(b.name));

        // Overfetch by |overrides| so that, after dropping stale DB rows whose id
        // is locally edited, at least `limit` DB items remain available to merge.
        const dbLimit = limit + overrideIds.size;

        return from(
          this.listResourcesQuery.execute({
            searchTerm,
            limit: dbLimit,
            lastItemName: cursor,
            type: typeFilter,
          })
        ).pipe(
          map((dbItems) =>
            this.mergeSorted(
              pendingPage,
              overridesPage,
              dbItems.filter((item) => !overrideIds.has(item.id)),
              limit
            )
          )
        );
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected processDeferredAttributes(_itemId: string): Observable<Partial<ResourceData>> {
    return of();
  }

  // ============================================================================
  // Staged-merge helpers
  // ============================================================================

  private matchesSearch(item: ResourceData, searchTerm: string | undefined): boolean {
    if (!searchTerm) return true;
    return item.name.toLowerCase().includes(searchTerm.toLowerCase());
  }

  /*
   * Catalog type restriction (mirror of the env filter the DB query already
   * honors): applied to the staged pending/override streams too, so a draft of
   * another type — notably the empty placeholder of the resource being created
   * (type `''`) — never leaks into a type-scoped catalog. Undefined = no filter.
   */
  private matchesType(item: ResourceData, typeFilter: string | undefined): boolean {
    return typeFilter == null || item.type === typeFilter;
  }

  /*
   * 3-way merge of pre-sorted streams. Picks the smallest name across the three
   * indices at each step; ties resolve pending → overrides → DB. The tie-break
   * is cosmetic — the cursor advances by name, so the next page resumes after
   * whichever item was emitted last (same accepted limitation as duplicate
   * names already have in the DB-only list).
   */
  private mergeSorted(
    pending: ResourceData[],
    overrides: ResourceData[],
    db: ResourceData[],
    limit: number
  ): ResourceData[] {
    const streams: ResourceData[][] = [pending, overrides, db];
    const idx = [0, 0, 0];
    const out: ResourceData[] = [];
    while (out.length < limit) {
      let pick = -1;
      for (let s = 0; s < streams.length; s++) {
        if (idx[s] >= streams[s].length) continue;
        if (pick === -1 || streams[s][idx[s]].name.localeCompare(streams[pick][idx[pick]].name) < 0) {
          pick = s;
        }
      }
      if (pick === -1) break;
      out.push(streams[pick][idx[pick]++]);
    }
    return out;
  }
}
